// Address suggestions on one of our own <input>s, from the Places API (New)
// AutocompleteSuggestion data API. It replaces google.maps.places.Autocomplete,
// which Google closed to new customers in March 2025 and no longer fixes.
//
// Unlike the PlaceAutocompleteElement widget, this keeps our input: its id,
// styling and React state stay as they are, and code that reads
// #searchLocationField's value (Settings, js/criteria-phrase.js) still works.
// Picking a suggestion writes the address into the input with a real 'input'
// event, so a React-controlled input's onChange sees it.
//
// Needs the Maps JavaScript API loaded (with or without the places library) and
// "Places API (New)" enabled for the key.

const MIN_CHARS = 3
const DEBOUNCE_MS = 250
const MAX_SUGGESTIONS = 5

const loadPlaces = async () => {
    const maps = window.google?.maps
    if (!maps) return null
    if (maps.places?.AutocompleteSuggestion) return maps.places
    if (maps.importLibrary) {
        try {
            return await maps.importLibrary('places')
        } catch (e) {
            console.log(e)
        }
    }
    return null
}

// React tracks a controlled input's value itself: set it through the native
// setter and fire 'input' so onChange runs with the new text.
const setInputValue = (input, value) => {
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
    if (setter) setter.call(input, value)
    else input.value = value
    input.dispatchEvent(new Event('input', { bubbles: true }))
}

/**
 * Attaches suggestions to input. Returns a function that removes them. Attaching
 * again to the same input replaces the earlier attachment.
 *
 * @param {HTMLInputElement} input
 * @param {{ countryCd?: string, onSelect?: ({ address, lat, lng }) => void }} options
 *        countryCd: ISO code ('sg', 'my', 'ae') the suggestions are limited to.
 *        onSelect: called after a pick, with the coordinates when Places has them.
 */
export const attachPlacesAutocomplete = (input, { countryCd, onSelect } = {}) => {
    if (!input || typeof window === 'undefined') return () => {}
    input.__placesAutocompleteDetach?.()

    let places = null
    let sessionToken = null
    let suggestions = []
    let active = -1
    let timer = null
    let requestSeq = 0
    let detached = false

    // On <body>, so a bottom sheet's overflow cannot clip it
    const list = document.createElement('ul')
    list.setAttribute('role', 'listbox')
    Object.assign(list.style, {
        position: 'absolute', zIndex: '10000', margin: '0', padding: '4px 0', listStyle: 'none',
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.12)', display: 'none', maxHeight: '260px', overflowY: 'auto',
        fontSize: '14px', color: '#111827',
    })
    document.body.appendChild(list)
    input.setAttribute('autocomplete', 'off')
    input.setAttribute('aria-autocomplete', 'list')

    const position = () => {
        const rect = input.getBoundingClientRect()
        list.style.left = `${rect.left + window.scrollX}px`
        list.style.top = `${rect.bottom + window.scrollY + 4}px`
        list.style.width = `${rect.width}px`
    }

    const hide = () => {
        list.style.display = 'none'
        active = -1
    }

    const render = () => {
        list.innerHTML = ''
        if (!suggestions.length) {
            hide()
            return
        }
        suggestions.forEach((suggestion, index) => {
            const prediction = suggestion.placePrediction
            const item = document.createElement('li')
            item.setAttribute('role', 'option')
            item.setAttribute('aria-selected', index === active ? 'true' : 'false')
            Object.assign(item.style, {
                padding: '10px 14px', cursor: 'pointer', lineHeight: '1.3',
                background: index === active ? '#fff7ed' : 'transparent',
            })
            const main = document.createElement('div')
            main.textContent = prediction.mainText?.text || prediction.text?.text || ''
            main.style.fontWeight = '600'
            const secondary = document.createElement('div')
            secondary.textContent = prediction.secondaryText?.text || ''
            Object.assign(secondary.style, { fontSize: '12px', color: '#6b7280' })
            item.appendChild(main)
            if (secondary.textContent) item.appendChild(secondary)
            // mousedown, not click: fires before the input's blur hides the list
            item.addEventListener('mousedown', (e) => {
                e.preventDefault()
                choose(index)
            })
            list.appendChild(item)
        })
        position()
        list.style.display = 'block'
    }

    const fetchSuggestions = async (text) => {
        const seq = ++requestSeq
        places = places || await loadPlaces()
        if (detached || !places?.AutocompleteSuggestion) return
        if (!sessionToken) sessionToken = new places.AutocompleteSessionToken()
        const request = { input: text, sessionToken }
        if (countryCd) request.includedRegionCodes = [countryCd.toLowerCase()]
        try {
            const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
            if (detached || seq !== requestSeq) return
            suggestions = (result?.suggestions || []).filter((s) => s.placePrediction).slice(0, MAX_SUGGESTIONS)
            active = -1
            render()
        } catch (e) {
            console.log(e)
            suggestions = []
            hide()
        }
    }

    const choose = async (index) => {
        const suggestion = suggestions[index]
        if (!suggestion) return
        const prediction = suggestion.placePrediction
        hide()
        suggestions = []
        let address = prediction.text?.text || ''
        let lat = null
        let lng = null
        try {
            const place = prediction.toPlace()
            await place.fetchFields({ fields: ['formattedAddress', 'location'] })
            address = place.formattedAddress || address
            lat = place.location?.lat() ?? null
            lng = place.location?.lng() ?? null
        } catch (e) {
            // Keep the suggestion's text; callers geocode it when they need coordinates
            console.log(e)
        }
        // A pick ends the billing session; the next keystrokes start a new one
        sessionToken = null
        if (detached) return
        setInputValue(input, address)
        onSelect?.({ address, lat, lng })
    }

    const onInput = (e) => {
        // Our own write after a pick: no new lookup
        if (!e.isTrusted) return
        clearTimeout(timer)
        const text = input.value.trim()
        if (text.length < MIN_CHARS) {
            suggestions = []
            hide()
            return
        }
        timer = setTimeout(() => fetchSuggestions(text), DEBOUNCE_MS)
    }

    const onKeyDown = (e) => {
        if (list.style.display === 'none' || !suggestions.length) return
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            active = (active + 1) % suggestions.length
            render()
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            active = active <= 0 ? suggestions.length - 1 : active - 1
            render()
        } else if (e.key === 'Enter') {
            // As before: Enter with nothing highlighted takes the first suggestion
            e.preventDefault()
            choose(active >= 0 ? active : 0)
        } else if (e.key === 'Escape') {
            hide()
        }
    }

    const onBlur = () => setTimeout(hide, 150)
    const onReposition = () => {
        if (list.style.display !== 'none') position()
    }

    input.addEventListener('input', onInput)
    input.addEventListener('keydown', onKeyDown)
    input.addEventListener('blur', onBlur)
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)

    const detach = () => {
        detached = true
        clearTimeout(timer)
        input.removeEventListener('input', onInput)
        input.removeEventListener('keydown', onKeyDown)
        input.removeEventListener('blur', onBlur)
        window.removeEventListener('resize', onReposition)
        window.removeEventListener('scroll', onReposition, true)
        list.remove()
        if (input.__placesAutocompleteDetach === detach) delete input.__placesAutocompleteDetach
    }
    input.__placesAutocompleteDetach = detach
    return detach
}
